import { useState, useRef } from "react";
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Paper,
  Chip,
  IconButton,
  InputBase,
  Fade,
  Popover,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// eslint-disable-next-line no-unused-vars
import {
  Phone,
  Video,
  MoreVertical,
  Paperclip,
  Smile,
  Send,
  MessageSquare,
  Image as ImageIcon,
  FileText,
} from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import EmojiPicker from "emoji-picker-react";
import WelcomeForm from "../Chat/AraChatbot/WelcomeForm";
import BookingForm from "../Chat/AraChatbot/BookingForm";

export default function TicketsChatPanel({
  theme,
  isTyping,
  chatRef,
  fileInputRef,
  selectedChat,
  messages,
  input,
  setInput,
  sendUserMessage,
  sendImageMessage,
  retryMessage,
  quickReplies,
  getDayLabel,
  tickets = [],
  openChat,
  showWelcomeForm,
  onStartNewChat,
  onWelcomeSubmit,
  showBooking,
  onBookingSubmit,
  onBookingCancel,
  sendDocumentMessage,
}) {
  const [previewImage, setPreviewImage] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const docInputRef = useRef(null);

  const TypingIndicator = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 0.5,
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: theme.palette.primary.main,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {selectedChat?.name?.[0] || "S"}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          px: 1.2,
          py: 0.8,
          borderRadius: 2,
          bgcolor: "#F3F4F6",
          "@keyframes bounce": {
            "0%, 60%, 100%": { transform: "translateY(0)" },
            "30%": { transform: "translateY(-3px)" },
          },
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "#9CA3AF",
              animation: `bounce 1.2s ${i * 0.15}s infinite ease-in-out`,
            }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: "#FAFBFC",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #F0F0F0",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: selectedChat
                ? theme.palette.primary.main
                : "#E5E7EB",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {selectedChat ? selectedChat.name[0] : "?"}
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color: "#1F2937",
              }}
            >
              {selectedChat
                ? selectedChat.name
                : "Select a conversation"}
            </Typography>

            <Typography
              sx={{
                fontSize: 11,
                color: selectedChat ? "#10B981" : "#9CA3AF",
                fontWeight: selectedChat ? 600 : 400,
                textTransform: selectedChat ? "uppercase" : "none",
                letterSpacing: selectedChat ? "0.06em" : "normal",
              }}
            >
              {selectedChat ? "Status: Online" : "No chat opened"}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          {selectedChat && (
            <>
              <IconButton
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  color: "#6B7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                <Phone size={18} />
              </IconButton>
              <IconButton
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  color: "#6B7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                <Video size={18} />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            sx={{
              width: 34,
              height: 34,
              color: "#6B7280",
              transition: "all 0.2s ease",
              "&:hover": {
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            <MoreVertical size={18} />
          </IconButton>
        </Stack>
      </Box>

      {/* Messages Area */}
      <Stack
        ref={chatRef}
        sx={{
          flex: 1,
          p: 3,
          gap: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.08)",
            borderRadius: 3,
          },
        }}
      >
        {!selectedChat ? (
          showWelcomeForm ? (
            /* Welcome Form — same as Ara chatbot */
            <Fade in timeout={500}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "92%", sm: 380 },
                    borderRadius: "20px",
                    overflow: "hidden",
                    bgcolor: "#fff",
                    boxShadow: `0 8px 40px ${alpha(theme.palette.primary.main, 0.1)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.06)}`,
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2.2,
                      pt: 2,
                      pb: 1.8,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.75)} 60%, ${alpha(theme.palette.primary.main, 0.5)} 100%)`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: "12px",
                          bgcolor: alpha("#fff", 0.2), backdropFilter: "blur(8px)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
                          <rect x="6" y="12" width="28" height="20" rx="6" fill="white" />
                          <circle cx="15" cy="22" r="2.5" fill={theme.palette.primary.main} />
                          <circle cx="25" cy="22" r="2.5" fill={theme.palette.primary.main} />
                          <rect x="16" y="27" width="8" height="2" rx="1" fill={theme.palette.primary.main} opacity="0.5" />
                        </svg>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
                          Ara Digital
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#69f0ae" }} />
                          <Typography sx={{ fontSize: 10.5, color: alpha("#fff", 0.8) }}>Online now</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                      Hello!
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: alpha("#fff", 0.85), mt: 0.3 }}>
                      Marketing Assistant - How can we help you today?
                    </Typography>
                  </Box>
                  <WelcomeForm onSubmit={onWelcomeSubmit} />
                </Box>
              </Box>
            </Fade>
          ) : (
            /* Empty State */
            <Fade in timeout={500}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                {/* Chat Icon — circle with subtle shadow */}
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    border: "1px solid #E5E7EB",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageSquare
                    size={28}
                    color="#9CA3AF"
                    strokeWidth={1.5}
                  />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 17,
                      color: "#1F2937",
                      mb: 0.75,
                    }}
                  >
                    Select a conversation
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: "#9CA3AF",
                      maxWidth: 320,
                      lineHeight: 1.55,
                    }}
                  >
                    Choose a channel from the list on the left to
                    start chatting with your support team or bot.
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )
        ) : (
          /* Chat Messages */
          (() => {
            let lastDate = "";
            return messages.map((m, i) => {
              const day = new Date(m.createdAt).toDateString();
              const show = day !== lastDate;
              if (show) lastDate = day;
              const isBot = m.from === "agent";

              return (
                <Fade in timeout={300} key={i}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {/* Date separator */}
                    {show && (
                      <Box
                        sx={{
                          alignSelf: "center",
                          px: 2,
                          py: 0.4,
                          borderRadius: 1.5,
                          bgcolor: "rgba(0,0,0,0.04)",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#9CA3AF",
                          }}
                        >
                          {getDayLabel(m.createdAt)}
                        </Typography>
                      </Box>
                    )}

                    {/* Message Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        alignSelf: isBot
                          ? "flex-start"
                          : "flex-end",
                        flexDirection: isBot ? "row" : "row-reverse",
                        maxWidth: "75%",
                      }}
                    >
                      {/* Bot Avatar */}
                      {isBot && (
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: theme.palette.primary.main,
                            fontSize: 12,
                            fontWeight: 700,
                            mt: 0.2,
                          }}
                        >
                          {selectedChat?.name?.[0] || "S"}
                        </Avatar>
                      )}

                      {/* Bubble + Timestamp column */}
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Paper
                          elevation={0}
                          onClick={() =>
                            m.status === "failed" && retryMessage()
                          }
                          sx={{
                            px: 2,
                            py: 1.25,
                            borderRadius: isBot
                              ? "12px 12px 12px 3px"
                              : "12px 12px 3px 12px",
                            whiteSpace: "pre-line",
                            cursor:
                              m.status === "failed"
                                ? "pointer"
                                : "default",
                            bgcolor: isBot
                              ? "#fff"
                              : "transparent",
                            background: isBot
                              ? "#fff"
                              : theme.palette.primary.main,
                            border: isBot
                              ? "1px solid #F0F0F0"
                              : "none",
                            boxShadow: isBot
                              ? "0 1px 3px rgba(0,0,0,0.04)"
                              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                            transition: "all 0.2s ease",
                          }}
                        >
                          {m.type === "image" ? (
                            <Box
                              component="img"
                              src={m.image}
                              sx={{
                                width: 230,
                                borderRadius: 1.5,
                                mb: 0.3,
                                cursor: "pointer",
                              }}
                              onClick={() => setPreviewImage(m.image)}
                            />
                          ) : m.type === "document" ? (
                            <Box
                              component="a"
                              href={m.fileUrl}
                              download={m.fileName}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                textDecoration: "none",
                                cursor: "pointer",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "8px",
                                  bgcolor: isBot
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : "rgba(255,255,255,0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <FileText
                                  size={18}
                                  color={isBot ? theme.palette.primary.main : "#fff"}
                                />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    color: isBot ? "#374151" : "#fff",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 160,
                                  }}
                                >
                                  {m.fileName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    color: isBot ? "#9CA3AF" : "rgba(255,255,255,0.7)",
                                  }}
                                >
                                  {m.fileSize}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 13.5,
                                lineHeight: 1.5,
                                color: isBot
                                  ? "#374151"
                                  : "#fff",
                              }}
                            >
                              {m.text}
                            </Typography>
                          )}
                        </Paper>

                        {/* Timestamp — below bubble */}
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "#9CA3AF",
                            mt: 0.3,
                            px: 0.3,
                            alignSelf: isBot ? "flex-start" : "flex-end",
                          }}
                        >
                          {m.time}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Quick Reply Options — after last bot message */}
                    {i === messages.length - 1 && isBot && (
                      <Box
                        sx={{
                          ml: 5,
                          mt: 1,
                          "@keyframes slideUp": {
                            from: { opacity: 0, transform: "translateY(8px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                          animation: "slideUp 0.3s ease forwards",
                        }}
                      >
                        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
                          {quickReplies.map((q, k) => (
                            <Chip
                              key={k}
                              label={q}
                              onClick={() => sendUserMessage(q)}
                              sx={{
                                height: 32,
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: 12,
                                width: "fit-content",
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                justifyContent: "flex-start",
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  transform: "translateX(2px)",
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Fade>
              );
            });
          })()
        )}

        {/* Booking Form — inline, same as Ara chatbot */}
        {showBooking && selectedChat && (
          <Box sx={{ ml: 3.5, maxWidth: 300 }}>
            <BookingForm
              userName={selectedChat.name}
              userPhone={selectedChat.phone}
              onSubmit={onBookingSubmit}
              onCancel={onBookingCancel}
            />
          </Box>
        )}

        {/* Typing Indicator — only during bot thinking */}
        {isTyping && selectedChat && <TypingIndicator />}
      </Stack>

      {/* Input Area */}
      <Box
        sx={{
          px: 2,
          py: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderTop: "1px solid #F0F0F0",
          bgcolor: "#fff",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            sendImageMessage(e.target.files[0]);
            e.target.value = "";
          }}
        />
        <input
          ref={docInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files[0]) sendDocumentMessage(e.target.files[0]);
            e.target.value = "";
          }}
        />

        {/* Attachment Icons */}
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={!selectedChat}
          sx={{
            color: "#6B7280",
            transition: "all 0.2s ease",
            "&:hover": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <ImageIcon size={20} />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => docInputRef.current?.click()}
          disabled={!selectedChat}
          sx={{
            color: "#6B7280",
            transition: "all 0.2s ease",
            "&:hover": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Paperclip size={20} />
        </IconButton>

        {/* Text Input */}
        <Box
          sx={{
            flex: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            bgcolor: "#F9FAFB",
            border: "1px solid #F0F0F0",
            display: "flex",
            alignItems: "center",
            transition: "all 0.2s ease",
            "&:focus-within": {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.06)}`,
            },
          }}
        >
          <InputBase
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendUserMessage(input);
              }
            }}
            placeholder="Type your message..."
            disabled={!selectedChat}
            sx={{
              fontSize: 13.5,
              color: "#374151",
              "& .MuiInputBase-input::placeholder": {
                color: "#9CA3AF",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Emoji */}
        <IconButton
          size="small"
          disabled={!selectedChat}
          onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
          sx={{
            color: emojiAnchorEl ? theme.palette.primary.main : "#6B7280",
            transition: "all 0.2s ease",
            "&:hover": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Smile size={20} />
        </IconButton>

        {/* Emoji Picker Popover */}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          PaperProps={{
            sx: {
              borderRadius: "14px",
              boxShadow: "0 16px 32px -8px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              overflow: "hidden",
            },
          }}
        >
          <EmojiPicker
            onEmojiClick={(emojiData) =>
              setInput((prev) => prev + emojiData.emoji)
            }
            autoFocusSearch={false}
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            height={380}
            width={320}
          />
        </Popover>

        {/* Send Button */}
        <IconButton
          onClick={() => sendUserMessage(input)}
          disabled={!selectedChat || !input.trim()}
          sx={{
            width: 38,
            height: 38,
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: theme.palette.primary.main,
              transform: "scale(1.05)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
            },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.primary.main, 0.25),
              color: "#fff",
            },
          }}
        >
          <Send size={18} />
        </IconButton>
      </Box>

      {/* Image Preview Overlay */}
      {previewImage && (
        <Box
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
            "@keyframes fadeInOverlay": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            animation: "fadeInOverlay 0.2s ease forwards",
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
            sx={{
              position: "fixed",
              top: 12,
              right: 12,
              width: 42,
              height: 42,
              bgcolor: "rgba(255,255,255,0.15)",
              borderRadius: "50%",
              backdropFilter: "blur(6px)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon sx={{ color: "#fff", fontSize: 24 }} />
          </IconButton>

          <Box
            component="img"
            src={previewImage}
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "92vw",
              maxHeight: "92vh",
              borderRadius: 2,
              boxShadow: 4,
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
